import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

export default function CreateShiftAttendanceModel({
  open,
  onClose,
  form,
  setForm,
  onSubmit,
  isEdit = false,
}) {
  const [loading, setLoading] = useState(false);

  const initialForm = {
    ShiftName: "",
    startTime: "",
    endTime: "",
    nightShift: "No",
  };

  const resetForm = () => {
    setForm(initialForm);
  };

  const handleModalClose = () => {
    if (!isEdit) {
      resetForm();
    }
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      await onSubmit();

      if (!isEdit) {
        resetForm();
      }

      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          }}
        >
          <Typography variant="h6" fontWeight={700} color="#fff">
            {isEdit ? "Edit Shift" : "Create Shift"}
          </Typography>

          <IconButton
            onClick={handleModalClose}
            sx={{ color: "#fff" }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
              },
              gap: 2,
              pt: 1,
            }}
          >
            <TextField
              fullWidth
              required
              label="Shift Name"
              value={form.ShiftName || ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  ShiftName: e.target.value,
                })
              }
            />

            <TimePicker
              label="Start Time"
              ampm
              minutesStep={1}
              value={
                form.startTime
                  ? dayjs(form.startTime, "hh:mm A")
                  : null
              }
              onChange={(value) =>
                setForm({
                  ...form,
                  startTime: value
                    ? value.format("hh:mm A")
                    : "",
                })
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />

            <TimePicker
              label="End Time"
              ampm
              minutesStep={1}
              value={
                form.endTime
                  ? dayjs(form.endTime, "hh:mm A")
                  : null
              }
              onChange={(value) =>
                setForm({
                  ...form,
                  endTime: value
                    ? value.format("hh:mm A")
                    : "",
                })
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />

            <TextField
              select
              required
              fullWidth
              label="Night Shift"
              value={form.nightShift || "No"}
              onChange={(e) =>
                setForm({
                  ...form,
                  nightShift: e.target.value,
                })
              }
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          </Box>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="outlined"
          onClick={handleModalClose}
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress
                size={18}
                color="inherit"
              />
            ) : null
          }
        >
          {loading
            ? isEdit
              ? "Updating..."
              : "Saving..."
            : isEdit
              ? "Update Shift"
              : "Create Shift"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}